// ─────────────────────────────────────────────
//  GlobalSheetImport — one click stages the daily sheet for ALL THREE
//  campuses (Wesley Chapel / Tampa / Carrollwood) at once. Admin-only:
//  cross-facility assignment + daily-board writes are admin-gated by the
//  security rules.
//
//  Uses its OWN sheet configuration (facilities/default.globalSheetImportConfig,
//  edited in Setup ▸ Google Sheet Import) — separate from each facility's
//  facility-specific sheetImportConfig. Flow: fetch once → parse per campus
//  room family (parseGlobalSheet) → per-facility summary review → Apply:
//  auto-create unmatched CRNAs (shared staff master, created once), re-resolve,
//  buildBoardWrites per facility, upsert assignment rows by OR name, and merge
//  the roster patch into each facility's daily board.
// ─────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, where } from "../firebase/sdk";
import { db } from "../firebase/config";
import Button from "./Button";
import Card from "./Card";
import tokens from "../theme/tokens";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useFacility } from "../hooks/useFacility";
import { useFacilities } from "../hooks/useFacilities";
import { importSheetValues } from "../features/sheet-import/sheetsClient";
import { parseGlobalSheet } from "../features/sheet-import/parseGlobalSheet";
import { facilityCoverage, sumCoverage, coverageLine } from "../features/sheet-import/importCoverage";
import { parseQgendaIdentity, qgendaDisplayIndex } from "../features/sheet-import/qgendaIdentity";
import ImportReviewQueue from "./ImportReviewQueue";
import RecentImportsList from "./RecentImportsList";
import useImportReviewActions, { applyBlockedBy } from "../hooks/useImportReviewActions";
import { buildBoardWrites } from "../features/sheet-import/buildBoardWrites";
import { toSheetDraftGrid } from "../features/sheet-import/toSheetDraftGrid";
import { linkCrnaChains } from "../features/sheet-import/linkCrnaChains";
import { detectDodCode } from "../features/sheet-import/detectDod";
import { facilityLabel, shortFacilityLabel, activeFacilityIds } from "../domain/facilities";
import { DEFAULT_FACILITY_ID } from "../config/roles";
import { importFilename, originalSheetTitle } from "../domain/sheetImports";
import { nextDayKey, defaultImportDate, canImportToDate, isTodayLocked, dayKeyOf } from "../domain/importDate";
import {
  createAssignment, deleteAssignmentRow,
  clearRoom, setDailyRoster, saveSheetImport, clearTravelingTransfers,
  pruneSheetImportsKeepLast, backfillSheetImportSnapshot,
  listenToSheetImports, getFacilitySurgeons, listStaffAliases,
} from "../firebase/firestore";

const { color, space } = tokens;

const fetchDocs = async (col, fid, extra = []) => {
  const snap = await getDocs(query(collection(db, col), where("facilityId", "==", fid), ...extra));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ⚠️ THE PLAN AND THE FACILITY LIST ARE READ AT DIFFERENT MOMENTS, AND APPLY IS
// DESTRUCTIVE. `plan.byFacility` is built ONCE, from the campus list as it
// stood when the sheet was parsed. `tenantFacilityIds` is derived from a LIVE
// facilities listener (useFacilities → activeFacilityIds) and can change while
// a reviewer is reading the summary — an administrator flips a campus to
// Active in another tab, or a facility doc finishes syncing. The apply loop
// then ran:
//       const { parsedRows, unassigned } = plan.byFacility[fid];
// which throws a TypeError on the first id the plan has never heard of.
//
// That throw lands MID-LOOP, and by then each completed iteration has already
// deleted its campus's assignment rows, cleared every room card and replaced
// the daily roster. So the abort left SOME real hospital boards wiped and the
// rest untouched — and the only trace was "Global apply failed — Cannot
// destructure property 'parsedRows' of undefined", which tells the charge nurse
// staring at an empty board precisely nothing.
//
// So the apply step is made TOTAL: every id present at apply time is classified
// BEFORE the first delete. A facility this plan cannot serve is SKIPPED with a
// named reason the operator is shown — never a thrown exception, and never a
// half-applied day. Pure and exported so the decision is testable without
// Firebase, React or a live sheet.
export function partitionFacilitiesForApply(plan, facilityIds) {
  const apply = [];
  const skipped = [];
  for (const fid of facilityIds || []) {
    const slice = plan?.byFacility?.[fid];
    if (!slice || typeof slice !== "object") {
      skipped.push({
        fid,
        reason: "not part of this import — it became active after the sheet was parsed. Re-fetch the sheet to include it.",
      });
    } else if (!Array.isArray(slice.parsedRows)) {
      // Present but unusable. A slice with no rows array cannot produce a write
      // payload, and clearing a board we then cannot refill is the worst
      // outcome on offer — worse than not importing at all. Refuse it the same
      // way, by name.
      skipped.push({
        fid,
        reason: "this import parsed no usable rows for it — its board was left untouched.",
      });
    } else {
      apply.push(fid);
    }
  }
  return { apply, skipped };
}

export default function GlobalSheetImport({ date, allDoctors = [], allCrnas = [] }) {
  // The whole roster, so the review queue can bind a cell to somebody the
  // ladder never proposed — see ImportReviewQueue's header for the "Liz B" case.
  const reviewStaffOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...(allCrnas || []), ...(allDoctors || [])]) {
      if (!p || !p.id || !p.name || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push({ id: p.id, name: p.name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [allCrnas, allDoctors]);
  const toast = useToast();
  const { realUser, isAdmin } = useAuth();
  // Global config rides on the home facility's doc (DEFAULT_FACILITY_ID, env-
  // configurable — no new collection/rules; blocker #8 fix).
  const { facility: defaultFacility } = useFacility(DEFAULT_FACILITY_ID);
  // All facilities — used to thread each campus's own room-family pattern
  // (facility.roomFamily) into the parser (blocker #4).
  const allFacilities = useFacilities();
  // THIS tenant's campuses, derived from the live facility docs. Every loop
  // below (fetch rooms, clear the day, apply, summarise) used to run over a
  // hardcoded ["default","tampa","carrollwood"], which is empty of meaning for
  // any other customer.
  const tenantFacilityIds = useMemo(
    () => activeFacilityIds(allFacilities || [], DEFAULT_FACILITY_ID),
    [allFacilities]
  );
  const cfg = defaultFacility?.globalSheetImportConfig || {};

  const [phase, setPhase] = useState("idle"); // idle | fetching | review | applying
  const [plan, setPlan] = useState(null); // { mdCall, byFacility, roomsByFacility, fileName }
  const [error, setError] = useState("");
  // "Clear Roster — All Facilities": two-click confirm for the destructive act.
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  // Recent GLOBAL imports (last 3) — mirrors the facility import's history
  // list. Global runs record one entry per facility; the "default" entry is
  // the run's representative.
  // Which DATE this import lands on. Defaults per the 06:30 rule: today's
  // board locks at 06:30 (the OR day is live) — later imports go to TOMORROW
  // unless an administrator consciously overrides.
  const [targetDate, setTargetDate] = useState(defaultImportDate());
  const [overrideLock, setOverrideLock] = useState(false);
  const [recentGlobal, setRecentGlobal] = useState([]);
  // The review queue's answers + the "I read it" gate (shared with the
  // per-facility importer so the two surfaces cannot disagree about what a
  // click means).
  const {
    answered, acknowledged, setAcknowledged, reset: resetReview,
    confirmAlias, createPerson, skipItem,
  } = useImportReviewActions();
  useEffect(() => {
    const unsub = listenToSheetImports(DEFAULT_FACILITY_ID, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.source === "global-sheet" || r.source === "scheduled" || r.source === "scheduled-internal")
        .sort((a, b) => String(b.importedAt?.seconds || 0) - String(a.importedAt?.seconds || 0))
        .slice(0, 3);
      setRecentGlobal(rows);
    }, () => {});
    return () => unsub();
  }, []);

  const clearAllFacilities = async () => {
    setClearingAll(true);
    try {
      let rowsCleared = 0, roomsReset = 0;
      for (const fid of tenantFacilityIds) {
        const [rows, rooms] = await Promise.all([
          fetchDocs("assignments", fid, [where("date", "==", date)]),
          fetchDocs("rooms", fid),
        ]);
        await Promise.all([
          ...rows.map((a) => deleteAssignmentRow(a.id)),
          ...rooms.map((r) => clearRoom(r.id)),
        ]);
        // CLEAR always hits the PAGE-SELECTED day (`date`) — the import
        // target-date must never leak in here (that exact leak once cleared
        // rows for today but reset the roster for tomorrow).
        await setDailyRoster(fid, date, {
          aicId: null, aicName: "", supervisors: [], crnas: [],
          crnaCallList: [], mdCallList: [], mdCallHome: [], locumLeaveTimes: {}, departed: {}, hiddenRooms: [],
        }, realUser?.uid);
        rowsCleared += rows.length;
        roomsReset += rooms.length;
      }
      // Traveling entries are day-scoped transfer docs — a cleared day has no
      // in-transit staff, so the 🚗 Traveling section must empty too.
      const transfersCleared = await clearTravelingTransfers(date);
      toast.success(`Cleared all ${tenantFacilityIds.length} facilities for ${date} — ${rowsCleared} assignment rows removed, ${roomsReset} rooms reset${transfersCleared ? `, ${transfersCleared} traveling entr${transfersCleared === 1 ? "y" : "ies"} cleared` : ""}.`);
    } catch (e) {
      console.error("[GlobalSheetImport] clear all failed:", e);
      toast.error("Couldn't fully clear every facility — check your connection and retry (re-running is safe).");
    } finally {
      setClearingAll(false);
      setConfirmClearAll(false);
    }
  };

  // Build the review plan from a 2D values grid (live fetch OR a stored
  // snapshot — identical parse path either way).
  const buildPlan = async (values, attendingFormats, fileName, fromSnapshot = false, qgendaValues = null) => {
    const facilityIds = tenantFacilityIds;
    const roomsByFacility = {};
    for (const fid of facilityIds) {
      roomsByFacility[fid] = await fetchDocs("rooms", fid);
    }
    const configByFacility = Object.fromEntries((allFacilities || []).map((f) => [f.id, f]));
    // ⚠️ THE ANSWERS HUMANS ALREADY GAVE, READ BEFORE ANYTHING ELSE. Without
    // this the same twenty questions arrive every morning and the system hovers
    // instead of converging. Aliases are per-campus, so every campus is loaded.
    const aliasLists = await Promise.all(facilityIds.map((fid) => listStaffAliases(fid).catch(() => [])));
    const aliases = aliasLists.flat();
    // The workbook's OWN identity map — "Chip Compton" is `Compton, Kang`. No
    // nickname dictionary can know that; the department republishes it daily.
    // A missing or restructured tab reads as empty and the ladder drops a rung.
    const qgenda = qgendaValues ? qgendaDisplayIndex(parseQgendaIdentity(qgendaValues)) : null;
    const parsed = parseGlobalSheet(values, {
      doctors: allDoctors, crnas: allCrnas, roomsByFacility, configByFacility, facilityIds,
      identity: { aliases, qgenda },
    });
    const total = facilityIds.reduce((n, f) => n + (parsed.byFacility[f]?.draft.length || 0), 0);
    if (total === 0) {
      setPhase("idle");
      setError("No rooms matched in any campus — wrong file/tab, or the sheet layout changed.");
      return false;
    }
    setPlan({
      ...parsed,
      roomsByFacility,
      aliases,
      qgendaValues: qgendaValues || null,
      rawValues: values,
      attendingFormats: attendingFormats || null,
      fileName,
      fromSnapshot,
    });
    resetReview();
    setPhase("review");
    return true;
  };

  const fetchAndParse = async () => {
    setError("");
    setPhase("fetching");
    try {
      const { values, file, attendingFormats, qgendaValues } = await importSheetValues({
        spreadsheetId: cfg.sheetId || undefined,
        titlePrefix: cfg.titlePrefix || undefined,
        tab: cfg.tab || undefined,
      });
      await buildPlan(values, attendingFormats, file?.name || "(fixed sheet id)", false, qgendaValues);
    } catch (err) {
      console.error("[GlobalSheetImport] fetch/parse failed:", err);
      setPhase("idle");
      setError("Global sheet import failed — " + (err?.message || "unknown error") + ".");
    }
  };

  // Re-run a PAST import from its stored snapshot — the data exactly as it was
  // captured at import time. NEVER touches the Google Sheets API. Legacy
  // records (pre-snapshot) have no stored grid — surface that instead of
  // silently fetching live data.
  const rerunSnapshot = async (rec) => {
    setError("");
    setPhase("fetching");
    try {
      let snap = {};
      try { snap = JSON.parse(rec.snapshotJson || "{}"); } catch { snap = {}; }
      let values = Array.isArray(snap.values) ? snap.values : null;
      let formats = snap.attendingFormats || null;
      let label = `${rec.filename || "Saved import"} (snapshot)`;
      if (!values) {
        // LEGACY record (pre-snapshot storage). Each day's sheet is its OWN
        // Drive file, frozen once the day passes — so fetch the record's exact
        // dated file ONCE, then store it as this record's immutable snapshot.
        const title = originalSheetTitle(rec.filename);
        if (!title) {
          setPhase("idle");
          setError("This record predates snapshot storage and carries no file name — run a fresh import instead.");
          return;
        }
        const fetched = await importSheetValues({ spreadsheetId: undefined, titlePrefix: title, tab: cfg.tab || undefined });
        values = fetched.values;
        formats = fetched.attendingFormats || null;
        label = `${rec.filename} (backfilled from the dated file)`;
        try {
          await backfillSheetImportSnapshot(rec.id, { values, attendingFormats: formats });
        } catch (bfErr) { console.error("Snapshot backfill save failed:", bfErr); }
      }
      await buildPlan(values, formats, label, true);
    } catch (err) {
      console.error("[GlobalSheetImport] snapshot re-run failed:", err);
      setPhase("idle");
      setError("Could not restore the snapshot — " + (err?.message || "unknown error") + ".");
    }
  };

  const apply = async () => {
    if (!plan) return;
    const gate = canImportToDate(targetDate, { isAdmin, adminOverride: overrideLock });
    if (!gate.ok) {
      setError(isAdmin
        ? "Today's board locked at 6:30 AM — tick the admin override to import onto it anyway, or target Tomorrow."
        : "Today's board locked at 6:30 AM. Import to Tomorrow, or ask an administrator to override.");
      setPhase("review");
      return;
    }
    // ⚠️ CLASSIFY EVERY CAMPUS BEFORE THE FIRST DESTRUCTIVE WRITE. This used to
    // be discovered by a TypeError partway through the loop — after real boards
    // had already been cleared. See partitionFacilitiesForApply.
    const { apply: applyIds, skipped } = partitionFacilitiesForApply(plan, tenantFacilityIds);
    const namesOf = (ids) =>
      ids.map((f) => shortFacilityLabel(facilityLabel(f))).join(", ") || "none";
    const skipLine = (list) =>
      list.map((s) => `${shortFacilityLabel(facilityLabel(s.fid))} — ${s.reason}`).join(" ");
    if (applyIds.length === 0) {
      // Nothing can be written, so nothing is cleared. Refusing BEFORE the first
      // delete is the entire point: an empty apply must be a no-op, not a wipe.
      setError(
        "Nothing to apply — no campus in this import matches the facilities that are active now. " +
        `${skipLine(skipped)} Re-fetch the sheet and review again.`
      );
      setPhase("review");
      return;
    }
    // Appended as each facility FINISHES, so on a mid-loop throw this is
    // exactly the set of boards that were rewritten — the list the catch below
    // has to be able to name.
    const appliedIds = [];
    setPhase("applying");
    try {
      // 1) THE IMPORT NEVER CREATES PEOPLE.
      //
      // It used to `addStaff({ name, role: "crna" })` for every unmatched CRNA
      // name. A sheet cell is written for a human reader, so it carries a shift
      // code, a request marker, a note, or a second person — and the whole
      // string matched no record, so the importer minted a colleague for it.
      // That is how the live directory came to hold "Charlotte Sanchez 7a-7a",
      // "Josh Larson 7 REQ", "Dave/Nick" and "Jim Slone 3, Gabi Swanson".
      //
      // ⚠️ AN UNRESOLVED NAME IS A QUESTION IN THE QUEUE ABOVE, NEVER A WRITE.
      // The row is still written with its `ahpName`, so the board shows who the
      // sheet says is in the room — it simply carries no staff id. Nobody
      // silently disappears from the board, and nobody silently appears in the
      // directory. A person who clicked "This is X" wrote an alias, and the
      // re-parse below picks it up on this very run.
      const crnaPool = allCrnas;
      const aliasesNow = await Promise.all(
        tenantFacilityIds.map((fid) => listStaffAliases(fid).catch(() => []))
      ).then((l) => l.flat());
      const qgendaNow = plan.qgendaValues
        ? qgendaDisplayIndex(parseQgendaIdentity(plan.qgendaValues))
        : null;

      // 2) Per facility: CLEAR the day first, then write the sheet fresh.
      //    The sheet is the source of truth — a global import REPLACES the day
      //    (no upsert-merge leftovers: stale rows, old departed map, old AIC).
      // Day-scoped traveling entries die with the day too.
      await clearTravelingTransfers(targetDate);
      let roomsTotal = 0;
      // The coverage of what ACTUALLY LANDED, re-counted after the aliases a
      // reviewer just confirmed. The number on the review screen was measured
      // before those clicks; reporting it again here would be reporting a
      // number that is no longer true.
      const covParts = [];
      // applyIds, not tenantFacilityIds — every id in here is already proven to
      // have a plan slice, so the destructure below cannot throw.
      for (const fid of applyIds) {
        const { parsedRows, unassigned } = plan.byFacility[fid];
        // Re-run the draft build so freshly-created CRNAs resolve to their ids.
        const grid = toSheetDraftGrid(parsedRows, {
          doctors: allDoctors,
          crnas: crnaPool,
          surgeons: [],
          rooms: plan.roomsByFacility[fid] || [],
          identity: { aliases: aliasesNow, qgenda: qgendaNow, facilityId: fid },
          sheetValues: plan.rawValues || null,
        });
        // Multi-room CRNAs: chain their cases by start time so both cards
        // show the route ("Next Room: X" / "Coming from: Y").
        covParts.push(facilityCoverage(grid, crnaPool, fid));
        const chained = linkCrnaChains(grid.rows, plan.roomsByFacility[fid] || []);
        // The facility's configured surgeons, so "Ahmadian + 1" / "TANDON" are
        // written LINKED to the configured spelling rather than as loose text.
        // Unreadable or unconfigured reads [] and the cell passes through.
        const facilitySurgeons = await getFacilitySurgeons(fid);
        const { assignments, roster } = buildBoardWrites(
          chained, grid.unassigned || unassigned, plan.mdCall, plan.notesLocums || [], facilitySurgeons, fid
        );

        // ⚠️ CLEAR ONLY ONCE THIS CAMPUS'S OWN WRITE PAYLOAD EXISTS. Everything
        // above — the draft build, the CRNA chaining, the facility's surgeon
        // list — can come back empty or malformed. Deleting a live board and
        // THEN discovering there is nothing to put back is unrecoverable from
        // this screen, so the delete is the last thing that happens before the
        // write, never the first thing that happens to the facility.
        if (!Array.isArray(assignments) || !roster) {
          skipped.push({
            fid,
            reason: "produced no write payload from this sheet — its board was left untouched.",
          });
          continue;
        }
        // CLEAR: delete the day's rows and reset every room card…
        const existing = await fetchDocs("assignments", fid, [where("date", "==", targetDate)]);
        await Promise.all([
          ...existing.map((a) => deleteAssignmentRow(a.id)),
          ...(plan.roomsByFacility[fid] || []).map((r) => clearRoom(r.id)),
        ]);
        // …then write the sheet's rows fresh.
        for (const a of assignments) {
          await createAssignment({ facilityId: fid, date: targetDate, ...a });
        }

        // Roster is REPLACED from the sheet (explicit empty defaults so no
        // stale departed/hidden/call-list fields survive from before).
        // Tampa: the DOD (their AIC) is marked by BOLD/COLOUR in the sheet's
        // attending column — resolve it from the fetched formatting.
        let dodPatch = {};
        if (fid === "tampa" && plan.attendingFormats) {
          // `parsedRows` is this facility's own slice, already destructured
          // above — reading plan.byFacility.tampa again re-introduced exactly
          // the unguarded lookup this fix removes.
          const code = detectDodCode(parsedRows, plan.attendingFormats);
          const dod = code && allDoctors.find((d) => String(d.shortCode || "").toUpperCase() === code);
          if (dod) dodPatch = { aicId: dod.id, aicName: dod.name };
        }
        await setDailyRoster(fid, targetDate, {
          aicId: null, aicName: "", supervisors: [], crnas: [],
          crnaCallList: [], mdCallList: [], mdCallHome: [], locumLeaveTimes: {}, departed: {}, hiddenRooms: [],
          ...roster,
          ...dodPatch,
        }, realUser?.uid);

        // IMMUTABLE snapshot: the raw sheet grid (+ attending formatting) as
        // captured at import time — Re-run restores THIS, never a live fetch.
        await saveSheetImport(fid, targetDate, {
          filename: importFilename(plan.fileName, targetDate),
          source: "global-sheet",
          snapshot: {
            rooms: assignments.length,
            values: plan.rawValues || null,
            attendingFormats: plan.attendingFormats || null,
          },
        });
        await pruneSheetImportsKeepLast(fid, 3);
        roomsTotal += assignments.length;
        // This campus is fully written. Record it only now — see appliedIds.
        appliedIds.push(fid);
      }
      // WHAT LANDED, IN THE SAME SENTENCE THE REVIEW SCREEN AND THE NIGHTLY RUN
      // LOG USE. A name the board could not resolve is something an
      // administrator has to act on, not something to discover days later from
      // an empty seat.
      const finalCov = sumCoverage(covParts);
      console.info(`[GlobalSheetImport] coverage — ${coverageLine(finalCov)}`);
      // Count and NAME what actually landed. This used to report
      // `tenantFacilityIds.length` — the number of campuses we set out to
      // import, not the number we wrote — so a run that skipped one still
      // announced "applied across 3 facilities".
      toast.success(
        `Global import applied — ${roomsTotal} rooms across ${appliedIds.length} ` +
        `facilit${appliedIds.length === 1 ? "y" : "ies"} (${namesOf(appliedIds)}). ${coverageLine(finalCov)}.`
      );
      // ⚠️ A SKIP IS NOT A FOOTNOTE, IT IS A CAMPUS WHOSE BOARD DID NOT CHANGE.
      // Raised as an error toast rather than folded into the success line: the
      // operator has to know which boards are still yesterday's before the OR
      // day starts. Silence here is the failure mode this fix exists to remove.
      if (skipped.length) {
        toast.error(
          `NOT imported — ${skipped.length} facilit${skipped.length === 1 ? "y" : "ies"} skipped. ` +
          `${skipLine(skipped)}`
        );
      }
      if (finalCov.needsYou > 0) {
        toast.error(
          `${finalCov.needsYou} name${finalCov.needsYou === 1 ? "" : "s"} still ${finalCov.needsYou === 1 ? "has" : "have"} no staff record and ${finalCov.needsYou === 1 ? "was" : "were"} NOT created: ` +
            finalCov.items.filter((i) => i.name).slice(0, 6).map((i) => i.name).join(", ") +
            (finalCov.needsYou > 6 ? `, +${finalCov.needsYou - 6} more` : "") +
            ". Their rooms still show the name; confirm or add them to link them."
        );
      }
      setPlan(null);
      setPhase("idle");
    } catch (err) {
      console.error("[GlobalSheetImport] apply failed:", err);
      setPhase("review");
      // ⚠️ NAME THE BOARDS. A mid-loop failure leaves the campuses already
      // processed REWRITTEN and the rest carrying yesterday's day. "Some
      // facilities may be partially applied" gave an operator nothing to act
      // on — not which board to go look at, not which one is still stale.
      // appliedIds is appended only after a facility is completely written, so
      // these two lists are exact.
      const notApplied = applyIds.filter((f) => !appliedIds.includes(f));
      setError(
        `Global apply failed — ${err?.message || "unknown error"}. ` +
        `APPLIED (boards rewritten): ${namesOf(appliedIds)}. ` +
        `NOT APPLIED (unchanged, still showing the previous day): ${namesOf(notApplied)}. ` +
        (skipped.length ? `Skipped: ${skipLine(skipped)} ` : "") +
        "Re-running is safe — each facility is cleared and rewritten from the sheet."
      );
    }
  };

  const summaryRow = (fid) => {
    const f = plan.byFacility?.[fid];
    // ⚠️ TOTAL, for the same reason the apply step is. This list is driven by
    // the LIVE facility list while `plan` is a snapshot from fetch time, so a
    // campus that just became active arrives here with no slice — and reading
    // `f.coverage` off undefined took the whole Setup page down with it.
    // The campus is still RENDERED, and rendered as excluded: dropping the row
    // would let "this import will not touch that board" look identical to
    // "there is nothing to do there".
    if (!f) {
      return (
        <div key={fid} style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${color.border.default}` }}>
          <strong style={{ width: 130, fontSize: 13 }}>{shortFacilityLabel(facilityLabel(fid))}</strong>
          <span style={{ fontSize: 11, color: color.accent.error, fontWeight: 700 }}>
            not in this import — became active after the sheet was parsed; re-fetch to include it
          </span>
        </div>
      );
    }
    const cov = f.coverage || { placed: 0, needsYou: 0 };
    const unknownDocs = f.unresolved?.doctorCodes || [];
    return (
      <div key={fid} style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${color.border.default}` }}>
        <strong style={{ width: 130, fontSize: 13 }}>{shortFacilityLabel(facilityLabel(fid))}</strong>
        <span style={{ fontSize: 12, color: color.text.secondary }}>
          {f.draft?.length || 0} room{(f.draft?.length || 0) === 1 ? "" : "s"} · {f.unassigned?.length || 0} bench ·{" "}
          {cov.placed} placed · {cov.needsYou} need you
          {unknownDocs.length ? ` · unknown MD codes: ${unknownDocs.join(", ")}` : ""}
        </span>
        {!f.found && <span style={{ fontSize: 11, color: color.accent.error, fontWeight: 700 }}>no rows found</span>}
      </div>
    );
  };

  const coverage = plan?.coverage || null;
  const applyBlocked = applyBlockedBy(coverage, acknowledged);
  // What the Apply button will ACTUALLY write, from the same function the apply
  // step uses. A button promising "all 3 facilities" while the plan can serve
  // two is the same lie the success toast used to tell.
  const applyableIds = plan ? partitionFacilitiesForApply(plan, tenantFacilityIds).apply : [];

  return (
    <Card style={{ marginBottom: space.md }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: color.text.primary }}>🌐 Import Global Google Sheet</div>
          <div style={{ fontSize: 12, color: color.text.muted }}>
            One click imports today's sheet into <strong>all three facilities</strong> (Wesley
            Chapel + Tampa + Carrollwood). Uses the <em>Global sheet</em> configuration —
            separate from the facility-specific import above.
          </div>
        </div>
        {phase === "idle" && (
          <>
            <Button onClick={fetchAndParse}>🌐 Import Global Google Sheet</Button>
            {confirmClearAll ? (
              <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: color.accent.error, fontWeight: 800 }}>
                  Clear ALL THREE facilities for {date}?
                </span>
                <Button variant="danger" onClick={clearAllFacilities} disabled={clearingAll}>
                  {clearingAll ? "Clearing…" : "Yes, clear all"}
                </Button>
                <Button variant="secondary" onClick={() => setConfirmClearAll(false)} disabled={clearingAll}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button variant="danger" onClick={() => setConfirmClearAll(true)}>
                🧹 Clear Roster — All Facilities
              </Button>
            )}
          </>
        )}
        {phase === "fetching" && <span style={{ fontSize: 12, color: color.text.muted }}>Reading sheet…</span>}
        {phase === "applying" && <span style={{ fontSize: 12, color: color.text.muted }}>Applying to all facilities…</span>}
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 10, fontSize: 12, color: color.accent.error, fontWeight: 600 }}>{error}</div>
      )}

      {/* Last 3 applied runs. Click RESTORES that run's stored immutable
          snapshot; it does NOT re-fetch the live sheet. */}
      {phase === "idle" && (
        <RecentImportsList records={recentGlobal} onRestore={rerunSnapshot} />
      )}

      {phase === "review" && plan && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: color.text.secondary, marginBottom: 6 }}>
            {plan.fromSnapshot
              ? <>Restored the saved snapshot <strong>{plan.fileName}</strong> — data exactly as captured at import time (no live fetch). Review, then apply:</>
              : <>Read <strong>{plan.fileName}</strong>. Review what lands where, then apply:</>}
          </div>
          {tenantFacilityIds.map(summaryRow)}
          {/* ⚠️ THE COVERAGE REPORT, BEFORE ANYTHING IS APPLIED. */}
          <ImportReviewQueue
            coverage={coverage} resolvedIds={answered}
            acknowledged={acknowledged} onAcknowledge={setAcknowledged}
            onConfirm={confirmAlias} onCreatePerson={createPerson} onSkip={skipItem}
            staffOptions={reviewStaffOptions}
          />
          {/* Which DAY this lands on. Today locks at 06:30 (live board);
              after that the default is Tomorrow — admin can override. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: color.text.primary }}>Apply to:</span>
            {[
              { key: dayKeyOf(), label: `Today (${dayKeyOf()})` },
              { key: nextDayKey(dayKeyOf()), label: `Tomorrow (${nextDayKey(dayKeyOf())})` },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTargetDate(t.key)}
                aria-pressed={targetDate === t.key}
                style={{
                  border: `1px solid ${targetDate === t.key ? color.accent.primary : color.border.default}`,
                  background: targetDate === t.key ? color.accent.primary : color.surface.raised,
                  color: targetDate === t.key ? "#fff" : color.text.secondary,
                  borderRadius: 9999, padding: "6px 14px", fontSize: 12, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {t.label}
              </button>
            ))}
            {targetDate === dayKeyOf() && isTodayLocked() && (
              isAdmin ? (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: color.accent.error, fontWeight: 700 }}>
                  <input type="checkbox" checked={overrideLock} onChange={(e) => setOverrideLock(e.target.checked)} />
                  Today locked since 6:30 AM — override and overwrite the LIVE board
                </label>
              ) : (
                <span style={{ fontSize: 12, color: color.accent.error, fontWeight: 700 }}>
                  Today locked since 6:30 AM — pick Tomorrow, or ask an administrator.
                </span>
              )
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <Button variant="secondary" onClick={() => { setPlan(null); setPhase("idle"); }}>Cancel</Button>
            <Button onClick={apply} disabled={Boolean(applyBlocked)}>
              {applyBlocked
                ? `Read the ${applyBlocked} open item${applyBlocked === 1 ? "" : "s"} first`
                : `Apply to ${applyableIds.length} facilit${applyableIds.length === 1 ? "y" : "ies"} — ${targetDate === dayKeyOf() ? "TODAY" : "TOMORROW"}`}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
